import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchData } from '../utils/apiServices';
import { getHebrewDayByNumber, getHebrewJewishMonthByNumber, toHebrewDate } from '../utils/jewishDates';
import { getDriveImageUrl, Header, LoadingSpinner, NotAllowed } from './_Common';
import { formatHebrewGrade, toDate } from '../utils/utilityFunctions';
import HDate from 'hebcal/src/hdate';

const MAX_ROWS_PER_PAGE = 5;
const formatEventDateHeader = (dateStr) => {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;

    const greg = toDate(d);
    const dayOfWeek = toHebrewDate(d, 'dddd');
    const hebDate = toHebrewDate(d, 'dd MM yyyy');

    return <>יום {dayOfWeek}, <span className="fw-normal">{hebDate}, {greg}</span></>
};

const EventsSlide = ({ events, gradesMap, duration, onDone }) => {
    const [pageIndex, setPageIndex] = useState(0);

    const groupedData = useMemo(() => {
        const map = {};

        events.forEach(ev => {
            const dateKey = ev.start ? ev.start.split('T')[0] : 'כללי';
            const gradeKey = ev.grade ?? 'all';

            if (!map[dateKey]) map[dateKey] = {};
            if (!map[dateKey][gradeKey]) map[dateKey][gradeKey] = [];

            map[dateKey][gradeKey].push(ev);
        });

        return Object.keys(map).sort().map(dateKey => {
            const gradeGroups = Object.keys(map[dateKey])
                .sort((a, b) => (parseInt(a, 10) || 999) - (parseInt(b, 10) || 999))
                .map(gradeId => ({
                    gradeId,
                    gradeName: gradesMap[gradeId]
                        ? `שכבה ${formatHebrewGrade(gradesMap[gradeId])}`
                        : gradeId === 'all'
                            ? 'כללי'
                            : `שכבה ${gradeId}`,
                    list: map[dateKey][gradeId]
                }));

            const combined = [];

            gradeGroups.forEach(group => {
                const existing = combined.find(x =>
                    x.list.length === group.list.length &&
                    x.list.every((ev, i) =>
                        ev.subject === group.list[i].subject &&
                        ev.isTest === group.list[i].isTest
                    )
                );

                if (existing) {
                    existing.gradeName = `${existing.gradeName.replace('שכבה ', '')}, ${group.gradeName.replace('שכבה ', '')}`;
                    existing.gradeId += `,${group.gradeId}`;
                }
                else { combined.push({ ...group }); }
            });

            return {
                dateKey,
                grades: combined
            };
        });
    }, [events, gradesMap]);

    const pages = useMemo(() => {
        const resultPages = [];
        let currentPage = [];
        let currentRows = 0;

        groupedData.forEach(({ dateKey, grades }) => {
            let gradeIndex = 0;

            while (gradeIndex < grades.length) {
                const spaceLeft = MAX_ROWS_PER_PAGE - currentRows;

                if (spaceLeft <= 0) {
                    resultPages.push(currentPage);
                    currentPage = [];
                    currentRows = 0;
                }

                const chunk = grades.slice(gradeIndex, gradeIndex + (MAX_ROWS_PER_PAGE - currentRows));

                currentPage.push({ dateKey, grades: chunk });
                currentRows += chunk.length;
                gradeIndex += chunk.length;

                if (currentRows >= MAX_ROWS_PER_PAGE) {
                    resultPages.push(currentPage);
                    currentPage = [];
                    currentRows = 0;
                }
            }
        });

        if (currentPage.length) resultPages.push(currentPage);

        return resultPages.length ? resultPages : [[]];
    }, [groupedData]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (pageIndex < pages.length - 1) {
                setPageIndex(pageIndex + 1);
            } else {
                onDone();
            }
        }, duration);

        return () => clearTimeout(timer);
    }, [pageIndex, pages.length, duration, onDone]);

    const activePage = pages[pageIndex] || [];
    const headerTitle = pages.length > 1 ? `אירועים קרובים <small class="fw-light">(${pageIndex + 1}/${pages.length})</small>` : 'אירועים קרובים';

    return (
        <div className="w-100 h-100 bg-transparent text-dark d-flex flex-column">
            <Header header={headerTitle} titleCss="display-4 fw-bold text-primary" />

            <div className="flex-grow-1 overflow-auto pe-2 d-flex flex-column gap-3 mt-2">
                {activePage.map(({ dateKey, grades }) => (
                    <div key={dateKey} className="bg-transparent rounded-4 overflow-hidden">
                        <div className="card-header bg-primary bg-gradient text-white fs-2 fw-bold py-3 px-4">
                            📅 {formatEventDateHeader(dateKey)}
                        </div>

                        <div className="card-body p-3 d-flex flex-column gap-1">
                            {grades.map(({ gradeId, gradeName, list }) => (
                                <div key={gradeId} className="row g-0 align-items-center mb-1">
                                    <div className="col-3 text-start pe-3">
                                        <span className="badge bg-secondary fs-3 py-3 w-100 text-center rounded-3 shadow-sm">{gradeName}</span>
                                    </div>

                                    <div className="col-9 d-flex flex-wrap gap-3 ps-3">
                                        {list.map((ev, idx) => (
                                            <div key={idx} className="p-3 rounded-3 bg-transparent border d-flex align-items-center gap-3">
                                                {ev.isTest && (<FontAwesomeIcon icon="fa-pen-to-square" className="text-primary fs-3" />)}
                                                <span className="fs-2 fw-semibold text-dark">{ev.subject}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            <div className="fs-3 fw-semibold ms-4 mb-4 my-auto"><FontAwesomeIcon icon="fa-solid fa-square" className="text-primary fa-sm" /> המידע מעמוד יומנים באתר.</div>
        </div>
    );
};


const AnalogClock = ({ date, size = 130 }) => {
    const center = size / 2;
    const seconds = date.getSeconds();
    const minutes = date.getMinutes();
    const hours = date.getHours() % 12;

    const secondDeg = seconds * 6;
    const minuteDeg = minutes * 6 + seconds * 0.1;
    const hourDeg = hours * 30 + minutes * 0.5;

    const hand = (length, deg, width, color) => {
        const rad = (deg - 90) * (Math.PI / 180);
        const x = center + length * Math.cos(rad);
        const y = center + length * Math.sin(rad);
        return <line x1={center} y1={center} x2={x} y2={y} stroke={color} strokeWidth={width} strokeLinecap="round" />;
    };

    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <circle cx={center} cy={center} r={center - 3} fill="white" stroke="#0d6efd" strokeWidth="3" />
            {[...Array(12)].map((_, i) => {
                const angle = i * 30;
                const rad = (angle - 90) * (Math.PI / 180);
                const x1 = center + (center - 8) * Math.cos(rad);
                const y1 = center + (center - 8) * Math.sin(rad);
                const x2 = center + (center - 14) * Math.cos(rad);
                const y2 = center + (center - 14) * Math.sin(rad);
                return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#333" strokeWidth="2" />;
            })}
            {hand(center * 0.5, hourDeg, 4, '#333')}
            {hand(center * 0.75, minuteDeg, 3, '#333')}
            {hand(center * 0.85, secondDeg, 1.5, '#dc3545')}
            <circle cx={center} cy={center} r="3.5" fill="#333" />
        </svg>
    );
};


const PicSlide = ({ pics, duration, onDone }) => {
    const randomPic = useMemo(
        () => pics?.length ? pics[Math.floor(Math.random() * pics.length)] : null,
        [pics]
    );

    useEffect(() => {
        const timer = setTimeout(onDone, duration);
        return () => clearTimeout(timer);
    }, [duration, onDone]);

    if (!randomPic) return null;

    const imgSrc = getDriveImageUrl(randomPic.src || randomPic.link || randomPic.url);

    return (
        <div className="w-100 h-100 bg-transparent text-dark d-flex flex-column align-items-center justify-content-center">
            <h1 className="display-4 fw-bold text-dark mb-3 text-center">{randomPic.name}</h1>
            <div className="d-flex align-items-center justify-content-center flex-grow-1 min-h-0 w-100">
                <img src={imgSrc} alt={randomPic.name} referrerPolicy="no-referrer"
                    className="current-info-image object-fit-contain" />
            </div>
            <div className="fs-3 fw-semibold ms-4 mb-4 my-auto"><FontAwesomeIcon icon="fa-solid fa-square" className="text-primary fa-sm" /> התמונה מעמוד גלריה באתר.</div>
        </div>
    );
};


const FileSlide = ({ file }) => {
    const isImage = file.fileType === 'Images';
    const imageUrl = getDriveImageUrl(file.link);

    return (
        <div className="w-100 h-100 bg-transparent text-dark d-flex flex-column align-items-center justify-content-center" dir="rtl">
            <h1 className="display-4 fw-bold text-primary mb-3 text-center">{file.name}</h1>

            <div className="position-relative flex-grow-1 min-h-0 w-100">
                {isImage ?
                    (<img src={imageUrl} alt={file.name} referrerPolicy="no-referrer" className="current-info-image position-absolute top-0 start-0" />)
                    :
                    (<iframe src={file.link?.replace(/\/view.*$/, '/preview')} title={file.name} className="current-info-image position-absolute top-0 start-0 border-0 bg-transparent" />)
                }
            </div>
        </div>
    );
};
const FilesSlide = ({ files, duration, onDone }) => {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (index < files.length - 1) { setIndex(index + 1); }
            else { onDone(); }
        }, duration);

        return () => clearTimeout(timer);
    }, [index, files.length, duration, onDone]);

    return <FileSlide file={files[index]} />;
};


const BirthdaysSlide = ({ birthdays, duration, onDone }) => {
    const today = new Date();

    const todayHebrew = new HDate(today);

    const groupedBirthdays = useMemo(() => {
        const groups = {};

        birthdays.forEach(student => {
            const key = `${student.hebrewBirthdayMonth}-${student.hebrewBirthdayDay}`;

            if (!groups[key]) { groups[key] = { day: student.hebrewBirthdayDay, month: student.hebrewBirthdayMonth, students: [] }; }

            groups[key].students.push(student);
        });

        const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const nextOccurrence = (g) => {
            const thisYear = new HDate(g.day, g.month, todayHebrew.getFullYear()).greg();
            return thisYear >= todayDateOnly ? thisYear : new HDate(g.day, g.month, todayHebrew.getFullYear() + 1).greg();
        };

        return Object.values(groups).sort((a, b) => nextOccurrence(a) - nextOccurrence(b));
    }, [birthdays]);

    const getDayTitle = (group) => {
        const birthdayDay = group.day;
        const birthdayMonth = group.month;

        const todayDay = todayHebrew.getDate();
        const todayMonth = todayHebrew.getMonth();

        const dateTitle = `${getHebrewDayByNumber(birthdayDay)} ${getHebrewJewishMonthByNumber(birthdayMonth)}`;

        if (todayDay === birthdayDay && todayMonth === birthdayMonth) return (<>היום <small className="fw-light opacity-50">({dateTitle})</small></>);

        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowHebrew = new HDate(tomorrow);

        if (tomorrowHebrew.getDate() === birthdayDay && tomorrowHebrew.getMonth() === birthdayMonth) return (<>מחר <small className="fw-light opacity-50">({dateTitle})</small></>);

        const dayAfter = new Date(today);
        dayAfter.setDate(dayAfter.getDate() + 2);
        const dayAfterHebrew = new HDate(dayAfter);

        if (dayAfterHebrew.getDate() === birthdayDay && dayAfterHebrew.getMonth() === birthdayMonth) return (<>מחרתיים <small className="fw-light opacity-50">({dateTitle})</small></>);

        return dateTitle;
    };

    useEffect(() => {
        const timer = setTimeout(onDone, duration);
        return () => clearTimeout(timer);
    }, [duration, onDone]);

    if (!birthdays?.length) return null;

    return (
        <div className="h-100 w-100 bg-transparent text-dark d-flex flex-column px-4" dir="rtl">

            <div className="d-flex align-items-center justify-content-between display-4 text-center mb-4">
                <span className="fw-bold text-dark">ימי הולדת קרובים 🎈</span>
                {/* <span className="fw-bold text-dark">מזל טוב! 🎉</span> */}
            </div>

            <div className="flex-grow-1 overflow-hidden w-100 px-5">
                {groupedBirthdays.map(group => (
                    <div key={`${group.month}-${group.day}`} className="mb-4">

                        <div className="text-end mb-3">
                            <span className="badge bg-primary bg-gradient fs-1 px-5 py-2 rounded-pill shadow">
                                {getDayTitle(group)}
                            </span>
                        </div>

                        <div className="row g-4">
                            {group.students.map((student, index) => (
                                <div key={index} className="col-3">
                                    <div className="card border-0 shadow-lg rounded-4 h-100">
                                        <div className="card-body d-flex align-items-center justify-content-center gap-3 p3">

                                            <div className="display-4">
                                                {student.gender === 'f' ? '👧' : '👦'}
                                            </div>

                                            <div className="text-center">
                                                <div className="fs-4 fw-bold text-primary">{student.name}</div>
                                                <div className="fs-5 fw-semibold text-dark">מכיתה {student.className}</div>
                                            </div>
                                            <div className="display-3">🎂</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default function CurrentInfo() {
    const [instDetails, setInstDetails] = useState({});
    const [slides, setSlides] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [gradesMap, setGradesMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [notAllowed, setNotAllowed] = useState(false);
    const [now, setNow] = useState(new Date());

    useEffect(() => {
        const timer = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const loadData = useCallback(async () => {
        const [cueinstDetails, eventsRes, picsRes, filesRes, birthdaysRes, gradesRes] = await Promise.all([
            fetchData('/api/InstDetails'),
            fetchData('/api/CurrentInfo/CalendarEvents'),
            fetchData('/api/CurrentInfo/Pics'),
            fetchData('/api/CurrentInfo/Files'),
            fetchData('/api/CurrentInfo/StudentsBirthdays'),
            fetchData('/api/Grades')
        ]);

        if (eventsRes?.error === 'Resource not found' || picsRes?.error === 'Resource not found' || filesRes?.error === 'Resource not found') {
            setNotAllowed(true);
            setLoading(false);
            return;
        }

        setInstDetails(cueinstDetails?.data || {});

        if (gradesRes?.data) {
            const gMap = {};
            gradesRes.data.forEach(g => { gMap[g.id] = g.name; });
            setGradesMap(gMap);
        }

        const events = eventsRes?.data || [];
        const pics = picsRes?.data || [];
        const files = filesRes?.data || [];
        const birthdays = birthdaysRes?.data || [];

        const queue = [];
        if (events.length) { queue.push({ type: 'events', data: events, duration: 5000 }); }
        if (pics.length) { queue.push({ type: 'random_pic', data: pics, duration: 2000 }); }
        if (files.length) { queue.push({ type: 'files', data: files, duration: 5000 }); }
        if (pics.length) { queue.push({ type: 'random_pic', data: pics, duration: 2000 }); }
        if (birthdays.length) queue.push({ type: 'birthdays', data: birthdays, duration: 5000 });
        if (pics.length) { queue.push({ type: 'random_pic', data: pics, duration: 2000 }); }

        setSlides(queue);
        setLoading(false);
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const circleCountRef = useRef(0);

    const goNext = useCallback(() => {
        setCurrentIndex(index => {
            const next = (index + 1) % slides.length;

            if (next === 0) {
                circleCountRef.current += 1;

                if (circleCountRef.current >= 2) {
                    circleCountRef.current = 0;
                    loadData();
                }
            }

            return next;
        });
    }, [slides.length, loadData]);

    if (loading) return <LoadingSpinner />;
    if (notAllowed) return <NotAllowed />;

    if (!slides.length) {
        return (
            <div className="d-flex align-items-center justify-content-center display-3 bg-transparent text-dark">
                אין נתונים להצגה
            </div>
        );
    }

    const currentSlide = slides[currentIndex];

    return (
        <div className="current-info w-100 bg-transparent overflow-hidden position-relative d-flex align-items-start justify-content-between gap-3" style={{ minHeight: 'calc(100vh - 100px)' }}>
            <div className="flex-grow-1 h-100 px-3 overflow-hidden mt-3" style={{ maxHeight: 'calc(100vh - 100px)' }}>
                {currentSlide.type === 'events' && (<EventsSlide events={currentSlide.data} gradesMap={gradesMap} duration={currentSlide.duration} onDone={goNext} />)}
                {currentSlide.type === 'random_pic' && (<PicSlide key={currentIndex} pics={currentSlide.data} duration={currentSlide.duration} onDone={goNext} />)}
                {currentSlide.type === 'files' && (<FilesSlide files={currentSlide.data} duration={currentSlide.duration} onDone={goNext} />)}
                {currentSlide.type === 'birthdays' && (<BirthdaysSlide birthdays={currentSlide.data} duration={currentSlide.duration} onDone={goNext} />)}
            </div>
            <div className="d-flex flex-column align-items-end mt-4" style={{ minWidth: '220px', maxWidth: '270px' }}>
                <img src={`${instDetails.homePageUrl}/images/${instDetails.logoFileName}`} className="img-fluid" style={{ maxWidth: '300px', maxHeight: '40vh', objectFit: 'contain' }} alt="Logo" />
                <div className="text-center mt-5 me-5 pe-4">
                    <div className="fs-4 fw-bold text-dark">יום {toHebrewDate(now, 'dddd')}</div>
                    <div className="fs-4 text-dark">{toHebrewDate(now, 'dd MM yyyy')}</div>
                    <div className="fs-4 text-dark">{toDate(now)}</div>
                    <div className="display-6 fw-bold text-primary mt-5">{toDate(now, 'HH:mm')}</div>
                    <div className="d-flex justify-content-center mt-2">
                        <AnalogClock date={now} />
                    </div>
                </div>
            </div>
        </div>
    );
}